import type { UploadedFileRef } from "@/types/api";
import type { IntakeStep } from "@/types/offer";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ValidationContext {
  /** Uploaded file refs (for fileUpload steps). */
  uploadedFiles: UploadedFileRef[];
  /** True while any file upload is still in flight. */
  uploadsInProgress: boolean;
}

/** Returns an error message, or null when the step's current answer is valid. */
export function validateStep(
  step: IntakeStep,
  value: unknown,
  ctx: ValidationContext,
): string | null {
  switch (step.type) {
    case "statement":
    case "review":
      return null;

    case "fileUpload": {
      if (ctx.uploadsInProgress) return "Please wait for your uploads to finish.";
      const min = step.minFiles ?? (step.required ? 1 : 0);
      if (ctx.uploadedFiles.length < min)
        return step.validation?.message ?? `Please upload at least ${min} file(s).`;
      return null;
    }

    case "email": {
      const v = typeof value === "string" ? value.trim() : "";
      if (!v) return step.required ? "Please enter your email." : null;
      if (!EMAIL_RE.test(v)) return step.validation?.message ?? "Please enter a valid email address.";
      return null;
    }

    case "singleChoice": {
      const v = typeof value === "string" ? value : "";
      if (step.required && !v) return step.validation?.message ?? "Please select an option.";
      return null;
    }

    case "multiChoice": {
      const arr = Array.isArray(value) ? (value as string[]) : [];
      if (step.required && arr.length === 0)
        return step.validation?.message ?? "Please select at least one option.";
      if (step.minSelections && arr.length < step.minSelections)
        return `Please select at least ${step.minSelections}.`;
      if (step.maxSelections && arr.length > step.maxSelections)
        return `Please select no more than ${step.maxSelections}.`;
      return null;
    }

    case "number": {
      const raw = typeof value === "string" || typeof value === "number" ? String(value) : "";
      if (!raw) return step.required ? "Please enter a number." : null;
      const n = Number(raw);
      if (Number.isNaN(n)) return "Please enter a valid number.";
      if (step.validation?.min != null && n < step.validation.min)
        return `Must be at least ${step.validation.min}.`;
      if (step.validation?.max != null && n > step.validation.max)
        return `Must be at most ${step.validation.max}.`;
      return null;
    }

    case "text":
    case "textarea": {
      const v = typeof value === "string" ? value.trim() : "";
      if (!v) return step.required ? "This field is required." : null;
      if (step.validation?.minLength != null && v.length < step.validation.minLength)
        return `Please enter at least ${step.validation.minLength} characters.`;
      if (step.validation?.maxLength != null && v.length > step.validation.maxLength)
        return `Please use ${step.validation.maxLength} characters or fewer.`;
      if (step.validation?.pattern) {
        try {
          if (!new RegExp(step.validation.pattern).test(v))
            return step.validation.message ?? "This value isn't in the expected format.";
        } catch {
          /* ignore invalid pattern config */
        }
      }
      return null;
    }

    default:
      return null;
  }
}
